/**
 * Support Drawer Body Component
 * Side drawer with mini FAQ/Resource library
 * Supports click+drag resize from left edge
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSupportDrawer } from '@stores';
import SupportDrawerHeader from './SupportDrawerHeader';
import { getHelpContent } from '@utils/helpContent';
import RequestAssistance from '@forms/components/RequestAssistance/RequestAssistance';

// Width constraints
const MIN_WIDTH = 538;
const MAX_WIDTH_PERCENT = 0.5; // 50% of viewport

const SupportDrawerBody = ({ currentPath, userRole }: { currentPath: string; userRole?: string }) => {
  const { isDrawerOpen, closeDrawer } = useSupportDrawer();
  const drawerRef = useRef<HTMLDivElement>(null);
  const initialFocusRef = useRef<HTMLInputElement>(null);
  const isSupportPage = (path: string) => path === '/support' || path.startsWith('/support/');

  const generateHelpContent = (path: string) => {
    const contextualContent = getHelpContent(path, userRole);

    return (
      <div className='support-drawer__layout'>
        <SupportDrawerHeader content='Support' />

        <div className='support-drawer__scroll-content'>
          {contextualContent && (
            <div className='request-assistance'>
              <h3 id='support-contextual-heading' className='request-assistance-intro__heading'>
                {contextualContent.header || 'Page Help'}
              </h3>
              <div className='support-drawer-contextual__content'>{contextualContent.body}</div>
            </div>
          )}

          <RequestAssistance showSelfHelp={!isSupportPage(path)} />
        </div>
      </div>
    );
  };

  // Resize state
  const [drawerWidth, setDrawerWidth] = useState(MIN_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(MIN_WIDTH);

  // Focus management and keyboard handling
  useEffect(() => {
    if (!isDrawerOpen) return;

    // Focus search input on open
    initialFocusRef.current?.focus();

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDrawer();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isDrawerOpen, closeDrawer]);

  // Trap focus within drawer
  useEffect(() => {
    if (!isDrawerOpen || !drawerRef.current) return;

    const drawer = drawerRef.current;
    const focusableElements = drawer.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    };

    drawer.addEventListener('keydown', handleTab);
    return () => drawer.removeEventListener('keydown', handleTab);
  }, [isDrawerOpen]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen]);

  // Handle resize drag
  const handleResizeMouseDown = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      setIsResizing(true);
      resizeStartX.current = event.clientX;
      resizeStartWidth.current = drawerWidth;
    },
    [drawerWidth]
  );

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (event: MouseEvent) => {
      // Calculate new width (dragging left increases width)
      const deltaX = resizeStartX.current - event.clientX;
      const maxWidth = window.innerWidth * MAX_WIDTH_PERCENT;
      const newWidth = Math.min(maxWidth, Math.max(MIN_WIDTH, resizeStartWidth.current + deltaX));
      setDrawerWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    // Add listeners to document for smooth dragging
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // Prevent text selection during drag
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizing]);

  // Reset width when drawer closes
  useEffect(() => {
    if (!isDrawerOpen) {
      // Optionally reset width on close, or keep it for next open
      setDrawerWidth(MIN_WIDTH);
    }
  }, [isDrawerOpen]);

  // const showFAQList = drawerState.searchQuery.trim() !== '' || drawerState.selectedCategory !== null;

  return (
    <>
      {/* Backdrop overlay */}
      {isDrawerOpen && <div className='support-drawer__backdrop' onClick={closeDrawer} aria-hidden='true' />}

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        className={`support-drawer ${isDrawerOpen ? 'support-drawer--open' : ''} ${isResizing ? 'support-drawer--resizing' : ''}`}
        style={{ width: `${drawerWidth}px` }}
        role='dialog'
        aria-label='Support resources'
        aria-modal='true'
        aria-hidden={!isDrawerOpen}
      >
        {/* Resize handle */}
        <div
          className='support-drawer__resize-handle'
          onMouseDown={handleResizeMouseDown}
          role='separator'
          aria-orientation='vertical'
          aria-label='Resize drawer'
          aria-valuenow={drawerWidth}
          aria-valuemin={MIN_WIDTH}
          aria-valuemax={window.innerWidth * MAX_WIDTH_PERCENT}
          tabIndex={0}
        />

        {generateHelpContent(currentPath)}
      </div>
    </>
  );
};

export default SupportDrawerBody;
